import type {
  FollowerReadyState,
  SyncedSessionContext,
  SyncedSessionEvent,
  SyncedSessionEventType,
  SyncedSessionListener,
  SyncedSessionManager,
  SyncedSessionTransport,
  Unsubscribe,
} from './types';
import {
  CMD,
  encodeFileChange,
  encodeTimecode,
  parseMessage,
} from './commands';

const TCREQ_COOLDOWN_MS = 10_000;

type ListenerMap = {
  [K in SyncedSessionEventType]: Set<SyncedSessionListener<K>>;
};

function makeListenerMap(): ListenerMap {
  return {
    play: new Set(),
    pause: new Set(),
    seek: new Set(),
    fileChange: new Set(),
    readyStateChange: new Set(),
    connect: new Set(),
    disconnect: new Set(),
    rewind: new Set(),
    fastForward: new Set(),
    home: new Set(),
    tcreq: new Set(),
    peerConnect: new Set(),
  };
}

export interface CreateSyncedSessionManagerOptions {
  context: SyncedSessionContext;
  transport: SyncedSessionTransport;
  /** Injectable `Date.now()` for tests. */
  now?: () => number;
}

/**
 * Pure state machine around the sync transport. No DOM, no React.
 *
 * Leader-only senders (`sendPlay`, `sendPause`, `sendSeek`, `sendFileChange`)
 * silently no-op when the role is follower. Follower-only senders
 * (`sendWait`, `sendReady`, `requestTimecode`) silently no-op when the role
 * is leader. This matches the behavior in `player_utils.js` and keeps the
 * UI layer from having to branch on role for every event.
 */
export function createSyncedSessionManager(opts: CreateSyncedSessionManagerOptions): SyncedSessionManager {
  const { context, transport } = opts;
  const now = opts.now ?? (() => Date.now());

  const listeners = makeListenerMap();
  let readyState: FollowerReadyState = 'disconnected';
  let lastTcReqAt = Number.NEGATIVE_INFINITY;
  let connected = false;
  let initialFileId = 0;

  function emit<T extends SyncedSessionEventType>(type: T, payload: Omit<Extract<SyncedSessionEvent, { type: T }>, 'type'> = {} as never): void {
    const event = { type, ...payload } as Extract<SyncedSessionEvent, { type: T }>;
    const set = listeners[type] as Set<SyncedSessionListener<T>>;
    set.forEach(cb => {
      try { cb(event); } catch (err) { console.error('[synced-session] listener threw', err); }
    });
  }

  function setReadyState(next: FollowerReadyState): void {
    if (readyState === next) return;
    // eslint-disable-next-line no-console
    console.log('[synced-session] readyState:', readyState, '→', next);
    readyState = next;
    emit('readyStateChange', { state: next } as never);
  }

  transport.onOpen(() => {
    connected = true;
    setReadyState('waiting');
    // The leader broadcasts the current file on connect so new followers
    // land on the right file. See `KCONNECT` handling in player_utils.js.
    if (context.role === 'leader' && Number.isFinite(initialFileId) && initialFileId > 0) {
      transport.send(encodeFileChange(initialFileId));
    }
    emit('connect', {} as never);
  });

  transport.onClose(() => {
    connected = false;
    setReadyState('disconnected');
    emit('disconnect', {} as never);
  });

  transport.onMessage((raw) => {
    const msg = parseMessage(raw);
    switch (msg.kind) {
      case 'play':
        emit('play', {} as never);
        break;
      case 'pause':
        emit('pause', {} as never);
        break;
      case 'seek':
        emit('seek', { timecode: msg.timecode, absolute: msg.absolute } as never);
        break;
      case 'fileChange':
        emit('fileChange', { fileId: msg.fileId } as never);
        break;
      case 'wait':
        // A peer (or we) just started buffering. Composite state → waiting.
        setReadyState('waiting');
        break;
      case 'allready':
        setReadyState('ready');
        break;
      case 'rew':
        emit('rewind', {} as never);
        break;
      case 'ff':
        emit('fastForward', {} as never);
        break;
      case 'home':
        emit('home', {} as never);
        break;
      case 'tcreq':
        emit('tcreq', {} as never);
        break;
      case 'connect':
        // Server reports a peer joined the room. Distinct from our own
        // transport-open `connect` event emitted by `transport.onOpen`.
        emit('peerConnect', {} as never);
        break;
      case 'disconnect':
      case 'ready':
      case 'note':
      case 'ping':
      case 'pong':
      case 'unknown':
        // Not surfaced to React layer; KPING handled inside the client.
        break;
    }
  });

  function sendIfLeader(msg: string): void {
    if (context.role !== 'leader') return;
    transport.send(msg);
  }

  function sendIfFollower(msg: string): void {
    if (context.role !== 'follower') return;
    transport.send(msg);
  }

  const manager: SyncedSessionManager = {
    context,
    get isEnabled() { return context.enabled; },
    get isLeader() { return context.role === 'leader'; },

    connect(fileId: number): void {
      if (!context.enabled) return;
      initialFileId = fileId;
      // The transport itself is started by the caller (SyncedSessionClient.start()).
      // The leader's KFILE broadcast fires from the transport.onOpen handler
      // registered above, which reads the captured `initialFileId`.
    },

    disconnect(): void {
      transport.close();
      connected = false;
      setReadyState('disconnected');
    },

    sendPlay: () => sendIfLeader(CMD.PLAY),
    sendPause: () => sendIfLeader(CMD.PAUSE),
    sendSeek: (timecode, absolute) => sendIfLeader(encodeTimecode(timecode, absolute)),
    sendFileChange: (fileId) => sendIfLeader(encodeFileChange(fileId)),
    sendRewind: () => sendIfLeader(CMD.REW),
    sendFastForward: () => sendIfLeader(CMD.FF),
    sendHome: () => sendIfLeader(CMD.HOME),

    // KWAIT / KREADY are sent from BOTH roles — the server keys readiness
    // off all connected sockets in the room and only emits KALLREADY when
    // every one of them (leader included) has sent KREADY. Matches the
    // legacy player_utils.js where 'seeked' / 'canplay' / 'canplaythrough'
    // unconditionally `sendSocketString('KREADY')`.
    sendWait: () => { if (context.enabled) transport.send(CMD.WAIT); },
    sendReady: () => { if (context.enabled) transport.send(CMD.READY); },
    requestTimecode: () => {
      if (context.role !== 'follower') return;
      const t = now();
      if (t - lastTcReqAt < TCREQ_COOLDOWN_MS) return;
      lastTcReqAt = t;
      transport.send(CMD.TCREQ);
    },

    on<T extends SyncedSessionEventType>(type: T, listener: SyncedSessionListener<T>): Unsubscribe {
      const set = listeners[type] as Set<SyncedSessionListener<T>>;
      set.add(listener);
      return () => { set.delete(listener); };
    },

    getReadyState: () => readyState,
  };

  // Silence TS when `connected` is flagged as unread in some builds — it's
  // captured by closures above and may be read by debug overlays later.
  void connected;

  return manager;
}

export { TCREQ_COOLDOWN_MS };
